import React, { useState } from "react";
import * as XLSX from "xlsx";
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetExpensesQuery } from "../../slices/expensesApiSlice";
import { useGetIncomesQuery } from "../../slices/incomesApiSlice";
import { useGetDepositsQuery } from "../../slices/depositsApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";

export default function BalanceStaffScreen() {
  const today = new Date().toISOString().split("T")[0];

  // Fetch data using RTK Query
  const { data: salesData, isLoading: salesLoading } = useGetSalesQuery();
  const { data: expensesData, isLoading: expensesLoading } = useGetExpensesQuery({
    startDate: today,
    endDate: today,
  });
  const { data: incomesData, isLoading: incomesLoading } = useGetIncomesQuery({
    startDate: today,
    endDate: today,
  });
  const { data: depositsData, isLoading: depositsLoading } = useGetDepositsQuery({
    startDate: today,
    endDate: today,
  });
  const { data: suppliesData, isLoading: suppliesLoading } = useGetSuppliesQuery();

  const [date1, setDate1] = useState(today);
  const [date2, setDate2] = useState(today);
  const [reportData, setReportData] = useState([]);
  const [totals, setTotals] = useState({
    totalSales: 0,
    totalExpenses: 0,
    totalIncomes: 0,
    totalDeposit: 0,
    totalPurchases: 0,
    totalCashInHand: 0,
  });

  // Generate dates between the range
  const getDatesBetween = (startDate, endDate) => {
    const dates = [];
    let currentDate = new Date(startDate);
   
    while (currentDate <= new Date(endDate)) {
      dates.push(currentDate.toISOString().split("T")[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  // Calculate and generate report
  const calculateReport = () => {
    const dates = getDatesBetween(date1, date2);

    const result = dates.map((date) => {
      const netSales = salesData?.sales?.filter(
        (sale) => sale.date && new Date(sale.date).toISOString().split("T")[0] === date
      ).reduce((sum, sale) => sum + Number(sale.totalPrice || 0), 0) || 0;

      const netExpenses = expensesData?.expenses?.filter(
        (expense) => expense.date && new Date(expense.date).toISOString().split("T")[0] === date
      ).reduce((sum, expense) => sum + Number(expense.amount || 0), 0) || 0;
      
      const cashCollected = incomesData?.incomes?.filter(
        (income) => income.date && new Date(income.date).toISOString().split("T")[0] === date
      ).reduce((sum, income) => sum + Number(income.amount || 0), 0) || 0;

      const depositTotal = depositsData?.deposits?.filter(
        (deposit) => deposit.date && new Date(deposit.date).toISOString().split("T")[0] === date
      ).reduce((sum, deposit) => sum + Number(deposit.amount || 0), 0) || 0;

      const netBank = depositTotal > 0 ? depositTotal * -1 : depositTotal * +1;

      const purchases = suppliesData?.supplies?.filter(
        (supply) => supply.date && new Date(supply.date).toISOString().split("T")[0] === date
      ).reduce((sum, supply) => sum + Number(supply.cardCost || 0), 0) * -1;

      const netCash = netSales + netExpenses;
      const difference = cashCollected - netCash;
      const cashInHand = netCash + netBank + purchases;

      return {
        date,
        netSales,
        netExpenses,
        netCash,
        cashCollected,
        difference,
        netBank,
        purchases,
        cashInHand,
      };
    });

    setReportData(result);
    calculateTotals(result);
  };

  // Calculate overall totals
  const calculateTotals = (data) => {
  const totalSales = data.reduce((sum, row) => sum + (row.netSales || 0), 0);
  const totalExpenses = data.reduce((sum, row) => sum + (row.netExpenses || 0), 0);
  const totalIncomes = data.reduce((sum, row) => sum + (row.cashCollected || 0), 0);
  const totalDeposit = data.reduce((sum, row) => sum + (row.netBank || 0), 0);
  const totalPurchases = data.reduce((sum, row) => sum + (row.purchases || 0), 0);
  const totalCashInHand = data.reduce((sum, row) => sum + (row.cashInHand || 0), 0);
  const totalNetCash = totalSales + totalExpenses;
  const totalDifference = totalIncomes - totalNetCash;

  setTotals({
    totalSales: totalSales.toFixed(2),
    totalExpenses: totalExpenses.toFixed(2),
    totalIncomes: totalIncomes.toFixed(2),
    totalDeposit: totalDeposit.toFixed(2),
    totalPurchases: totalPurchases.toFixed(2),
    totalCashInHand: totalCashInHand.toFixed(2),
    totalNetCash: totalNetCash.toFixed(2),
    totalDifference: totalDifference.toFixed(2),
  });
};

   // Export to Excel
  const exportToExcel = () => {
    const exportData = reportData.map((row) => ({
      Date: row.date,
      NetSales: row.netSales.toFixed(2),
      NetExpenses: row.netExpenses.toFixed(2),
      NetCash: row.netCash.toFixed(2),
      CashCollected: row.cashCollected.toFixed(2),
      Difference: row.difference.toFixed(2),
      NetBank: row.netBank.toFixed(2),
      Purchases: row.purchases.toFixed(2),
      CashInHand: row.cashInHand.toFixed(2),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Balance Report");
    XLSX.writeFile(workbook, "BalanceReport.xlsx");
  };

  return (
    <div className="balance-report">
      <h1>Balance Report</h1>

      <div className="date-range">
        <label>
          Start Date:
          <input type="date" value={date1} onChange={(e) => setDate1(e.target.value)} />
        </label>
        <label>
          End Date:
          <input type="date" value={date2} onChange={(e) => setDate2(e.target.value)} />
        </label>
      </div>

      <button onClick={calculateReport} className="calculate-button">
        Calculate Report
      </button>

      {salesLoading || expensesLoading || incomesLoading || depositsLoading || suppliesLoading ? (
        <div>Loading data...</div>
      ) : (
        <>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Net Sales</th>
                <th>Net Inc/Exp</th>
                <th>Net Cash</th>
                <th>Cash Collected</th>
                <th>Difference</th>
                <th>Net Bank</th>
                <th>Purchases</th>
                <th>Cash In Hand</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((row) => (
                <tr key={row.date}>
                  <td>{row.date}</td>
                  <td>{row.netSales.toFixed(2)}</td>
                  <td>{row.netExpenses.toFixed(2)}</td>
                  <td>{row.netCash.toFixed(2)}</td>
                  <td>{row.cashCollected.toFixed(2)}</td>
                  <td>{row.difference.toFixed(2)}</td>
                  <td>{row.netBank.toFixed(2)}</td>
                  <td>{row.purchases.toFixed(2)}</td>
                  <td>{row.cashInHand.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
            <tr>
           <td><strong>Total</strong></td>
           <td><strong>{totals.totalSales}</strong></td>
          <td><strong>{totals.totalExpenses}</strong></td>
          <td><strong>{totals.totalNetCash}</strong></td>
          <td><strong>{totals.totalIncomes}</strong></td>
          <td><strong>{totals.totalDifference}</strong></td>
          <td><strong>{totals.totalDeposit}</strong></td>
           <td><strong>{totals.totalPurchases}</strong></td>
           <td><strong>{totals.totalCashInHand}</strong></td>
          </tr>
          </tfoot>
          </table>
          
          <button onClick={exportToExcel} className="export-button"   style={{ display: 'none' }}>
            Export to Excel
          </button>
        </>
      )}
    </div>
  );
}